/* eslint-disable no-redeclare */
/* eslint-disable @typescript-eslint/no-unused-vars */

import { Component } from 'react';
import { createPortal } from 'react-dom';
import FocusTrap from 'react-focus-trap';
import classNames from 'classnames';
import LoaderMask from './LoaderMask';
import canUseDOM from '../utilities/canUseDOM';

const domAvailable = canUseDOM();

const modalRoot = domAvailable ? document.getElementById('app-modals') : null;

interface Modal {
  el: HTMLDivElement;
}

interface ModalProps {
  style?: React.CSSProperties;
  className?: string;
  escapeToClose?: boolean;
  isVisible?: boolean;
  title?: string;
  showClose?: boolean;
  handleClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  isLoading?: boolean;
}

class Modal extends Component<ModalProps, {}> {
  constructor(props: ModalProps) {
    super(props);
    if (modalRoot) this.el = document.createElement('div');
    this.closeOnEscape = this.closeOnEscape.bind(this);
  }

  componentDidMount() {
    if (modalRoot) modalRoot.appendChild(this.el);
  }

  componentWillUnmount() {
    if (modalRoot) modalRoot.removeChild(this.el);
  }

  componentDidUpdate(prevProps: ModalProps) {
    if (this.props.isVisible !== prevProps.isVisible) {
      if (this.props.isVisible) {
        document.body.classList.add('modal-open');
      } else {
        document.body.classList.remove('modal-open');
      }
    }
  }

  closeOnEscape(e?: React.SyntheticEvent) {
    const { escapeToClose, handleClose } = this.props;

    if (handleClose && escapeToClose) {
      handleClose();
    }
  }

  render() {
    if (!modalRoot) {
      return null;
    }

    const {
      style,
      className,
      isVisible = false,
      title,
      showClose,
      handleClose,
      children,
      footer
    } = this.props;

    const modalDialogClasses = classNames('modal-dialog', className);

    return createPortal(
      <>
        <div
          className={`modal ${isVisible ? 'show' : ''}`}
          style={{ display: isVisible ? 'block' : 'none' }}
          onClick={handleClose}
          tabIndex={-1}
        >
          <div
            style={style}
            className={modalDialogClasses}
            role="document"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <FocusTrap active={isVisible} onExit={this.closeOnEscape}>
              <div className="modal-content">
                {this.props.isLoading && <LoaderMask type="absolute" />}
                {title && (
                  <div className="modal-header">
                    <h5 className="modal-title">{title}</h5>
                    {showClose && handleClose && (
                      <button
                        type="button"
                        className="close"
                        onClick={handleClose}
                        data-dismiss="modal"
                        aria-label="Close"
                      >
                        <span aria-hidden="true">&times;</span>
                      </button>
                    )}
                  </div>
                )}
                <div className="modal-body">{children}</div>

                {footer && <div className="modal-footer">{footer}</div>}
              </div>
            </FocusTrap>
          </div>
        </div>
        <div className={`modal-backdrop ${isVisible ? 'show' : 'hidden'}`} />
      </>,
      this.el
    );
  }
}

export default Modal;
